<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { SYSTEM_FIELDS } from 'dashboard/composables/useCsvColumnMapping';
import ContactImportPreviewTable from './ContactImportPreviewTable.vue';

const props = defineProps({
  headers: {
    type: Array,
    required: true,
  },
  previewRows: {
    type: Array,
    required: true,
  },
  mapping: {
    type: Object,
    required: true,
  },
  isValid: {
    type: Boolean,
    default: false,
  },
  allAutoDetected: {
    type: Boolean,
    default: false,
  },
  autoDetectedHeaders: {
    type: Set,
    default: () => new Set(),
  },
});

const emit = defineEmits(['update:mapping']);

const { t } = useI18n();

// Which system fields are already mapped
const mappedSystemFields = computed(() => {
  return new Set(Object.values(props.mapping));
});

// Get preview value for a header column
const getPreviewValue = headerIndex => {
  if (props.previewRows.length === 0) return '';
  const row = props.previewRows[0];
  const value = row[headerIndex] || '';
  return value.length > 30 ? `${value.slice(0, 30)}...` : value;
};

// Handle mapping change for a header
const handleMappingChange = (csvHeader, systemField) => {
  const newMapping = { ...props.mapping };

  // Clear this system field from any other header
  if (systemField) {
    Object.entries(newMapping).forEach(([key, value]) => {
      if (value === systemField && key !== csvHeader) {
        delete newMapping[key];
      }
    });
    newMapping[csvHeader] = systemField;
  } else {
    delete newMapping[csvHeader];
  }

  emit('update:mapping', newMapping);
};

// Check if a system field option should be disabled
const isFieldDisabled = (systemFieldKey, currentHeader) => {
  if (props.mapping[currentHeader] === systemFieldKey) return false;
  return mappedSystemFields.value.has(systemFieldKey);
};
</script>

<template>
  <div class="space-y-4">
    <!-- Title -->
    <div>
      <h2 class="text-lg font-semibold text-n-slate-12">
        {{ t('CONTACT_IMPORT.MAPPING.TITLE') }}
      </h2>
      <p class="mt-1 text-sm text-n-slate-11">
        {{ t('CONTACT_IMPORT.MAPPING.DESCRIPTION') }}
      </p>
    </div>

    <!-- Auto-detected banner -->
    <div
      v-if="allAutoDetected"
      class="p-3 bg-n-teal-2 rounded-lg border border-n-teal-6"
    >
      <p class="text-sm text-n-teal-11 font-medium">
        {{ t('CONTACT_IMPORT.MAPPING.ALL_AUTO_DETECTED') }}
      </p>
    </div>

    <!-- Mapping table -->
    <div class="border border-n-slate-6 rounded-lg overflow-hidden">
      <!-- Table header -->
      <div
        class="grid grid-cols-[1fr_1fr_1fr] gap-2 px-4 py-2.5 bg-n-slate-2 border-b border-n-slate-6"
      >
        <span class="text-xs font-semibold text-n-slate-11 uppercase tracking-wide">
          {{ t('CONTACT_IMPORT.MAPPING.YOUR_COLUMN') }}
        </span>
        <span class="text-xs font-semibold text-n-slate-11 uppercase tracking-wide">
          {{ t('CONTACT_IMPORT.MAPPING.MAPS_TO') }}
        </span>
        <span class="text-xs font-semibold text-n-slate-11 uppercase tracking-wide">
          {{ t('CONTACT_IMPORT.MAPPING.PREVIEW') }}
        </span>
      </div>

      <!-- Mapping rows -->
      <div
        v-for="(header, index) in headers"
        :key="header"
        class="grid grid-cols-[1fr_1fr_1fr] gap-2 px-4 py-2.5 border-b border-n-slate-6 last:border-b-0 items-center"
        :class="mapping[header] ? 'bg-n-background' : 'bg-n-alpha-1'"
      >
        <!-- CSV column name -->
        <div class="flex items-center gap-2 min-w-0">
          <span class="text-sm font-mono text-n-slate-12 truncate">
            {{ header }}
          </span>
          <span
            v-if="autoDetectedHeaders.has(header)"
            class="text-[10px] px-1.5 py-0.5 rounded bg-n-teal-3 text-n-teal-11 font-medium flex-shrink-0"
          >
            {{ t('CONTACT_IMPORT.MAPPING.AUTO_DETECTED') }}
          </span>
        </div>

        <!-- System field select -->
        <select
          :value="mapping[header] || ''"
          class="w-full px-2.5 py-1.5 text-sm border border-n-slate-6 rounded-md bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-9 focus:border-n-blue-9"
          @change="handleMappingChange(header, $event.target.value)"
        >
          <option value="">
            {{ t('CONTACT_IMPORT.MAPPING.SKIP_COLUMN') }}
          </option>
          <option
            v-for="field in SYSTEM_FIELDS"
            :key="field.key"
            :value="field.key"
            :disabled="isFieldDisabled(field.key, header)"
          >
            {{ t(field.labelKey) }}
            <template v-if="isFieldDisabled(field.key, header)">
              {{ t('CONTACT_IMPORT.MAPPING.ALREADY_MAPPED') }}
            </template>
          </option>
        </select>

        <!-- Preview value -->
        <span class="text-sm text-n-slate-9 truncate">
          {{ getPreviewValue(index) }}
        </span>
      </div>
    </div>

    <!-- Validation message -->
    <div
      v-if="!isValid"
      class="p-3 bg-n-amber-2 rounded-lg border border-n-amber-6"
    >
      <p class="text-sm text-n-amber-11 font-medium">
        {{ t('CONTACT_IMPORT.MAPPING.VALIDATION_ERROR') }}
      </p>
    </div>

    <!-- Preview table -->
    <ContactImportPreviewTable
      v-if="Object.keys(mapping).length > 0"
      :mapping="mapping"
      :headers="headers"
      :preview-rows="previewRows"
    />
  </div>
</template>
